import { useId } from "react";

/**
 * Deterministic per-colony colors so every colony is instantly
 * recognizable in dropdowns and on ticket cards. Colors are derived
 * from the colony id via a stable string hash → hue angle. Because we
 * pick a hue in a full 360° space (not modulo a small palette), TWO
 * different colonies practically never collide — which was the bug
 * where "Spruce" and "Parkview Homes" ended up with the same green.
 *
 * The hue is offset by GOLDEN_RATIO so consecutively-hashed ids land
 * far apart on the color wheel. Saturation is fixed (feels calm) and
 * lightness alternates a touch between odd/even hue-buckets so
 * two nearby hues still look meaningfully different.
 */
const GOLDEN_STEP = 137.508; // golden-angle spread across 360°

function hashStr(s) {
  let h = 5381;
  for (let i = 0; i < (s || "").length; i++) {
    // djb2 — good spread on short strings like UUIDs.
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function colonyColor(id) {
  const h = hashStr(id);
  const hue = ((h * GOLDEN_STEP) % 360 + 360) % 360;
  // Alternate lightness slightly between neighbouring buckets so
  // two colonies whose hues happen to be close still look distinct.
  const isEven = h % 2 === 0;
  const dotL = isEven ? 45 : 38;
  const bgL = isEven ? 93 : 90;
  const textL = isEven ? 28 : 24;
  const borderL = isEven ? 78 : 72;
  const sat = 65;
  return {
    dotStyle: { backgroundColor: `hsl(${hue} ${sat}% ${dotL}%)` },
    badgeStyle: {
      backgroundColor: `hsl(${hue} 75% ${bgL}%)`,
      color: `hsl(${hue} 60% ${textL}%)`,
      borderColor: `hsl(${hue} 60% ${borderL}%)`,
    },
  };
}

/** Small colored badge for a colony name. */
export const ColonyBadge = ({ id, name, className = "" }) => {
  const generatedId = useId().replace(/:/g, "");
  const c = colonyColor(id);
  return (
    <span
      className={`inline-flex min-w-0 max-w-full items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[11px] font-semibold ${className}`}
      style={c.badgeStyle}
      data-testid={`colony-badge-${id ?? "none"}-${generatedId}`}
    >
      <span className="w-1.5 h-1.5 shrink-0 rounded-full" style={c.dotStyle} />
      <span className="min-w-0 [overflow-wrap:anywhere]">{name}</span>
    </span>
  );
};

/** Colored dot + name, for dropdown items. */
export const ColonyDotName = ({ id, name, compact = false }) => {
  const c = colonyColor(id);
  if (compact) return <span className="inline-flex min-w-0 max-w-full items-center gap-2" title={name}><span className="h-2 w-2 shrink-0 rounded-full" style={c.dotStyle} /><span className="min-w-0 truncate">{name}</span></span>;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-2 h-2 rounded-full" style={c.dotStyle} />
      {name}
    </span>
  );
};
